import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import { ensureCategory } from '../utils/taxonomy';
import SearchableSelect from './SearchableSelect';
import '../styles/forms.css';
import '../styles/ManageSubjects.css';
import Icon from '@mdi/react';
import { mdiPlus, mdiSquareEditOutline, mdiDeleteOutline } from '@mdi/js';

const emptyForm = () => ({
    name: '',
    default_category_id: null,
});

const COLUMNS = ['name', 'default_category_name', 'transaction_count'];

const compareSubjects = (a, b, field) => {
    if (field === 'transaction_count') return (a.transaction_count || 0) - (b.transaction_count || 0);
    return (a[field] || '').localeCompare(b[field] || '');
};

const ManageSubjects = () => {
    const [subjects, setSubjects] = useState([]);
    const [categories, setCategories] = useState([]);
    const [editing, setEditing] = useState(null);
    const [formData, setFormData] = useState(emptyForm());
    const [panelOpen, setPanelOpen] = useState(false);
    const [error, setError] = useState('');
    const [sortField, setSortField] = useState('name');
    const [sortDir, setSortDir] = useState('asc');
    const [filter, setFilter] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [subjectsRes, categoriesRes] = await Promise.all([
                api.get('/subjects'),
                api.get('/categories'),
            ]);
            setSubjects(subjectsRes.data);
            const sortedCategories = categoriesRes.data
                .map(c => ({ ...c, label: c.full_name || c.name }))
                .sort((a, b) => a.label.localeCompare(b.label));
            setCategories(sortedCategories);
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to load data.');
        }
    };

    const openCreatePanel = () => {
        setEditing(null);
        setFormData(emptyForm());
        setError('');
        setPanelOpen(true);
    };

    const openEditPanel = (subject) => {
        setEditing(subject);
        setFormData({
            name: subject.name,
            default_category_id: subject.default_category_id,
        });
        setError('');
        setPanelOpen(true);
    };

    const closePanel = () => {
        setPanelOpen(false);
        setEditing(null);
        setError('');
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCategorySelect = async (option) => {
        if (!option) {
            setFormData(prev => ({ ...prev, default_category_id: null }));
            return;
        }
        if (option.isNew) {
            try {
                const ensured = await ensureCategory(option.label);
                setCategories(prev => (prev.some(c => c.id === ensured.value)
                    ? prev
                    : [...prev, { id: ensured.value, name: ensured.label, label: ensured.label }]));
                option = { value: ensured.value, label: ensured.label };
            } catch (err) {
                console.error('Error creating category:', err);
                return;
            }
        }
        setFormData(prev => ({ ...prev, default_category_id: option.value }));
    };

    const handleSort = (field) => {
        if (!COLUMNS.includes(field)) return;
        if (field === sortField) {
            setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir('asc');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.name.trim()) {
            setError('Subject name is required.');
            return;
        }

        try {
            if (editing) {
                await api.patch(`/subjects/${editing.id}`, formData);
            } else {
                await api.post('/subjects', formData);
            }
            closePanel();
            await fetchData();
        } catch (err) {
            setError('Failed to save subject: ' + (err.response?.data?.error || err.message));
        }
    };

    const handleDelete = async (subject) => {
        const count = subject.transaction_count || 0;
        if (count > 0) {
            window.alert(
                `Cannot delete "${subject.name}": it has ${count} transaction(s). ` +
                `Reassign or delete those transactions first.`
            );
            return;
        }
        if (!window.confirm(`Are you sure you want to delete "${subject.name}"?`)) {
            return;
        }
        try {
            await api.delete(`/subjects/${subject.id}`);
            await fetchData();
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to delete subject.';
            window.alert(msg);
            console.error('Error deleting subject:', err);
        }
    };

    const sortedSubjects = [...subjects]
        .filter(s => !filter || s.name.toLowerCase().includes(filter.toLowerCase()))
        .sort((a, b) => {
            const cmp = compareSubjects(a, b, sortField);
            return sortDir === 'asc' ? cmp : -cmp;
        });

    const arrow = (field) => sortField === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

    const categoryOptions = categories.map(c => ({ value: c.id, label: c.label }));
    const panelTitle = editing ? 'Edit Subject' : 'New Subject';

    return (
        <div className="card">
            <div className="transaction-table subjects-table">
                <h2 className="page-title">Manage Subjects</h2>

                <div className="header">
                    <div className="filter-group subjects-filter">
                        <input
                            type="text"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Filter by name…"
                            className="filter-input"
                        />
                    </div>
                    <div className="buttons-bar">
                        <button
                            className="btn btn--primary"
                            onClick={openCreatePanel}
                            type="button"
                        >
                            <Icon path={mdiPlus} size={0.8} /> New Subject
                        </button>
                    </div>
                </div>

                <div className="table-wrapper">
                    <table>
                        <thead>
                            <tr>
                                <th className="sortable" onClick={() => handleSort('name')}>Name{arrow('name')}</th>
                                <th className="sortable" onClick={() => handleSort('default_category_name')}>Default Category{arrow('default_category_name')}</th>
                                <th className="sortable num" onClick={() => handleSort('transaction_count')}>Transactions{arrow('transaction_count')}</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedSubjects.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="empty-row">
                                        {filter
                                            ? `No subjects match "${filter}".`
                                            : 'No subjects yet. Click "New Subject" to create one.'}
                                    </td>
                                </tr>
                            )}
                            {sortedSubjects.map((subject) => {
                                const count = subject.transaction_count || 0;
                                const blocked = count > 0;
                                return (
                                    <tr key={subject.id}>
                                        <td>{subject.name}</td>
                                        <td>{subject.default_category_name || '—'}</td>
                                        <td className="num">{count}</td>
                                        <td>
                                            <button
                                                className="btn btn--ghost"
                                                onClick={() => openEditPanel(subject)}
                                                title="Edit"
                                            >
                                                <Icon path={mdiSquareEditOutline} size={0.75} />
                                            </button>
                                            <button
                                                className="btn btn--ghost"
                                                onClick={() => handleDelete(subject)}
                                                title={blocked ? 'Cannot delete: subject has transactions' : 'Delete'}
                                                disabled={blocked}
                                            >
                                                <Icon path={mdiDeleteOutline} size={0.75} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {panelOpen && (
                    <div className="edit-panel create-panel">
                        <div className="panel-header">
                            <h3>{panelTitle}</h3>
                            <button onClick={closePanel}>×</button>
                        </div>

                        <form onSubmit={handleSubmit} className="form">
                            {error && <div className="error">{error}</div>}

                            <div className="form-group">
                                <label>Name
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Enter subject name"
                                        required
                                    />
                                </label>
                            </div>

                            <div className="form-group">
                                <label>Default Category
                                    <SearchableSelect
                                        options={categoryOptions}
                                        onSelect={handleCategorySelect}
                                        value={formData.default_category_id}
                                        placeholder="Search category…"
                                        allowCreate={true}
                                    />
                                </label>
                            </div>

                            <div className="form-buttons">
                                <button type="button" className="btn btn--secondary" onClick={closePanel}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn--primary">
                                    {editing ? 'Save Changes' : 'Create Subject'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ManageSubjects;